import {
  AreaChart,
  Area,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { fmtPrice } from "../lib/format";

// ---- Recharts config — hoisted to avoid re-creating objects every render
const CHART_MARGIN = { top: 12, right: 24, left: 0, bottom: 0 };
const TICK_STYLE = {
  fill: "var(--text-muted)",
  fontSize: 10,
  fontFamily: "JetBrains Mono",
};
const CURSOR_STYLE = { stroke: "var(--accent-hex)", strokeDasharray: "3 3" };

export function PriceChart({ data, loading, trendUp }) {
  if (loading) {
    return (
      <div
        data-testid="chart-loading"
        className="h-full w-full flex items-center justify-center"
      >
        <div className="font-mono-tight text-xs uppercase tracking-[0.3em] text-muted-soft">
          · Loading price series ·
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div
        data-testid="chart-empty"
        className="h-full w-full flex items-center justify-center"
      >
        <div className="font-mono-tight text-xs uppercase tracking-[0.3em] text-muted-soft">
          · No historical data ·
        </div>
      </div>
    );
  }

  // recharts expects oldest → newest left-to-right
  const series = [...data]
    .filter((d) => d.close != null)
    .sort((a, b) => (a.date > b.date ? 1 : -1))
    .map((d) => ({ date: d.date, close: d.close }));

  const color = trendUp ? "var(--positive)" : "var(--negative)";
  const values = series.map((d) => d.close);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = (max - min) * 0.08 || 1;
  const yDomain = [min - pad, max + pad];

  return (
    <div
      data-testid="price-chart"
      className="h-full w-full font-mono-tight"
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={series} margin={CHART_MARGIN}>
          <defs>
            <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.25} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--grid)"
            vertical={false}
          />
          <XAxis
            dataKey="date"
            tick={TICK_STYLE}
            stroke="var(--border-hex)"
            axisLine={false}
            tickLine={false}
            minTickGap={40}
            tickFormatter={(v) => String(v).slice(5)}
          />
          <YAxis
            domain={yDomain}
            tick={TICK_STYLE}
            stroke="var(--border-hex)"
            axisLine={false}
            tickLine={false}
            width={56}
            tickFormatter={(v) => Number(v).toFixed(0)}
          />
          <Tooltip cursor={CURSOR_STYLE} content={<TerminalTooltip />} />
          <Area
            type="monotone"
            dataKey="close"
            stroke={color}
            strokeWidth={1.8}
            fill="url(#areaFill)"
            isAnimationActive
            animationDuration={500}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function TerminalTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div
      data-testid="chart-tooltip"
      className="bg-[var(--surface)] border border-soft px-3 py-2 font-mono-tight"
    >
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-soft">
        {p.date}
      </p>
      <p className="num text-sm text-[var(--text-primary)]">
        {fmtPrice(p.close)}
      </p>
    </div>
  );
}
